import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import moment from "moment";
import { 
  Row, 
  Col, 
  Card, 
  Avatar, 
  Table, 
  Tag, 
  Typography, 
  Space,
  Statistic, 
  Input, 
  Badge,
  List,
  Button,
  message,
  Drawer,
  Form,
  Select
} from 'antd';
import { 
  UserOutlined,
  BookOutlined,
  TeamOutlined,
  FieldTimeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  HourglassOutlined,
  BarChartOutlined,
  EnvironmentOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  LoginOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import MonthlyHoursTrends from '../../components/MonthlyHoursTrends';
import getUserIdFromToken from '../../Utils/authUtils';
import { useTranslation } from 'react-i18next';
import './Teacher.css';

const { Title, Text } = Typography;

function TeacherDashboard() {
  const { t, i18n } = useTranslation();
  const userId = getUserIdFromToken();
  const [teacher, setTeacher] = useState(null);
  const [todaysClasses, setTodaysClasses] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [error, setError] = useState(null);
  const [totalMonthlyHours, setTotalMonthlyHours] = useState(0);
  const [futureSearchText, setFutureSearchText] = useState('');
  const [pastSearchText, setPastSearchText] = useState('');
  const [registrationsCount, setRegistrationsCount] = useState([]);

  // Add these state variables at the top of your component (with other useState declarations)
  const [selectedClass, setSelectedClass] = useState(null);
  const [savedAttendances, setSavedAttendances] = useState({});
  const [attendance, setAttendance] = useState({});
  const [homework, setHomework] = useState({});
  const [editMode, setEditMode] = useState({});
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [registeredStudents, setRegisteredStudents] = useState([]);
  const [classDetails, setClassDetails] = useState(null);

  // Feedback states
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [selectedClassFeedback, setSelectedClassFeedback] = useState(null);
  const [studentsInClass, setStudentsInClass] = useState([]);
  const [studentId, setStudentId] = useState(null);
  const [isFeedbackApproved, setIsFeedbackApproved] = useState(false);
  const [feedbackData, setFeedbackData] = useState({
    warmup: '',
    task_1: '',
    task_2: '',
    task_3: '',
    homework: '',
    reflection: '',
    comment: ''
  });
  const API_URL = process.env.REACT_APP_API_URL;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: '2-digit' };
    return new Intl.DateTimeFormat(i18n.language, options as any).format(date).toUpperCase();
  };

  const formatDateTime = (dateTimeString) => {
    const date = new Date(dateTimeString);
    const options = { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' };
    return new Intl.DateTimeFormat(i18n.language, options as any).format(date).toUpperCase();
  };

  const formatMinutesToHours = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}:${remainingMinutes.toString().padStart(2, '0')} ${t('hr')}`;
  };

  const formatScheduleHour = (scheduleHourString) => {
    try {
      const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value, 10));
      return `${hours}:${minutes.toString().padStart(2, '0')} ${t('hr')}`;
    } catch (error) {
      console.error('Invalid schedule hour value:', scheduleHourString);
      return 'Invalid Time';
    }
  };

    const formatTimeHHMM = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle different time formats
      if (timeString.includes(':')) {
        const [hours, minutes] = timeString.split(':');
        return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return 'N/A';
    }
  };


     const fetchTeacherClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes/teacher/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const today = moment().format('YYYY-MM-DD');
      const todayClasses = response.data.filter(cls =>
        moment(cls.date).format('YYYY-MM-DD') === today
      );

      setTodaysClasses(todayClasses);
    } catch (error) {
      console.error('Error fetching today’s classes:', error);
    }
  };

  useEffect(() => {

    const fetchTeacherData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teachers/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTeacher(response.data);
      } catch (error) {
        console.error('Error fetching teacher data:', error);
        setError(t('failedToFetchTeacherData'));
      }
    };
     const fetchRegistrationsCount = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/registrations/registrations-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRegistrationsCount(response.data);
      } catch (error) {
        console.error('Error fetching registrations count:', error);
        setError(t('failedToFetchRegistrationsCount'));
      }
    };

    const fetchTotalMonthlyHours = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/classes/teacher/totalhours/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTotalMonthlyHours(response.data.totalHours);
      } catch (error) {
        console.error('Error fetching total monthly hours:', error);
        setError(t('failedToFetchTotalMonthlyHours'));
      }
    };

    const fetchClassGroups = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/class-groups/teacher/${userId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClassGroups(response.data);
      } catch (error) {
        console.error('Error fetching class groups:', error);
        setError(t('failedToFetchClassGroups'));
      }
    };


    fetchTeacherData();
    fetchClassGroups();
    fetchRegistrationsCount();
    fetchTotalMonthlyHours();
     fetchTeacherClasses();
  }, [userId, API_URL, t]);

  // Attendance handlers
const handleShowModal = async (cls) => {
  setSelectedClass(cls);
  try {
    const token = localStorage.getItem('token');
    
    // Fetch registered students
    const studentsResponse = await axios.get(
      `${API_URL}/registrations/classgroup/${cls.groupid}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setRegisteredStudents(studentsResponse.data);
    setClassDetails(cls);

    // Fetch existing attendance
    const response = await axios.get(`${API_URL}/attendance/${cls.classid}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const existingAttendance = response.data.reduce((acc, record) => {
      acc[record.student_id] = {
        attendance_id: record.attendance_id,
        status: record.status,
        comment: record.comment,
        homework: record.homework,
        timestamp: record.timestamp,
        edited_at: record.edited_at
      };
      return acc;
    }, {});

    setSavedAttendances(existingAttendance);
    setAttendance({});
    setHomework({});
  } catch (error) {
    console.error('Error fetching attendance:', error);
  }
};

const handleEdit = (studentId) => {
  setEditMode(prev => ({ ...prev, [studentId]: true }));
  setAttendance(prev => ({
    ...prev,
    [studentId]: {
      status: savedAttendances[studentId].status,
      comment: savedAttendances[studentId].comment
    }
  }));
  setHomework(prev => ({
    ...prev,
    [studentId]: savedAttendances[studentId].homework
  }));
};

const handleAttendanceChange = (studentId, field, value) => {
  setAttendance(prev => ({
    ...prev,
    [studentId]: { ...prev[studentId], [field]: value }
  }));
};

const handleHomeworkChange = (studentId, value) => {
  setHomework(prev => ({ ...prev, [studentId]: value }));
};

const handleSaveAttendance = async (studentId) => {
  if (!attendance[studentId]?.status) {
    message.warning('Please select an attendance status');
    return;
  }

  setSavingInProgress(true);
  try {
    const token = localStorage.getItem('token');
    const attendanceData = [{
      student_id: studentId,
      status: attendance[studentId].status,
      comment: attendance[studentId].comment || '',
      homework: homework[studentId] || 'N/A'
    }];

    await axios.post(
      `${API_URL}/attendance`,
      {
        groupid: classDetails.groupid,
        classid: selectedClass.classid,
        attendance: attendanceData
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setSavedAttendances(prev => ({
      ...prev,
      [studentId]: {
        ...attendance[studentId],
        homework: homework[studentId],
        timestamp: new Date().toISOString()
      }
    }));

    setAttendance(prev => {
      const newState = { ...prev };
      delete newState[studentId];
      return newState;
    });
    setHomework(prev => {
      const newState = { ...prev };
      delete newState[studentId];
      return newState;
    });

    message.success('Attendance saved');
  } catch (error) {
    console.error('Error saving attendance:', error);
    message.error('Failed to save attendance');
  } finally {
    setSavingInProgress(false);
  }
};

const handleUpdate = async (studentId) => {
  try {
    const token = localStorage.getItem('token');
    const savedRecord = savedAttendances[studentId];
    
    if (!savedRecord || !savedRecord.attendance_id) {
      message.error('Unable to update: Record not found');
      return;
    }

    const response = await axios.put(
      `${API_URL}/attendance/${savedRecord.attendance_id}`,
      {
        status: attendance[studentId].status,
        comment: attendance[studentId].comment || '',
        homework: homework[studentId] || 'N/A'
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    setSavedAttendances(prev => ({
      ...prev,
      [studentId]: { ...response.data }
    }));

    setEditMode(prev => {
      const newState = { ...prev };
      delete newState[studentId];
      return newState;
    });

    message.success('Attendance updated');
  } catch (error) {
    console.error('Error updating attendance:', error);
    message.error('Failed to update attendance');
  }
};

// Feedback handlers
const handleOpenFeedback = async (slot) => {
  try {
    setSelectedClassFeedback(slot);
    setFeedbackVisible(true);

    const token = localStorage.getItem('token');
    const groupid = slot.groupid || slot.class_group_id || slot.classid;

    const studentsResponse = await axios.get(`${API_URL}/registrations/classgroup/${groupid}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setStudentsInClass(studentsResponse.data);
    setFeedbackData({
      warmup: '',
      task_1: '',
      task_2: '',
      task_3: '',
      homework: '',
      reflection: '',
      comment: ''
    });
    setIsFeedbackApproved(false);
    setStudentId(null);
  } catch (err) {
    console.error('Failed to fetch feedback or students:', err);
    message.error('Failed to load data for this class');
  }
};

const handleCloseFeedback = () => {
  setFeedbackVisible(false);
  setSelectedClassFeedback(null);
  setStudentId(null);
  setFeedbackData({
    warmup: '',
    task_1: '',
    task_2: '',
    task_3: '',
    homework: '',
    reflection: '',
    comment: ''
  });
};

const handleSubmitFeedback = async () => {
  if (!studentId || !selectedClassFeedback) {
    message.warning('Please select a student and a class.');
    return;
  }

  if (isFeedbackApproved) {
    message.warning('Cannot update. Feedback is already approved.');
    return;
  }

  const token = localStorage.getItem('token');
  const payload = {
    classid: selectedClassFeedback.classid,
    teacher_id: selectedClassFeedback.teacher_id,
    student_id: studentId,
    ...feedbackData,
  };

  try {
    const checkRes = await axios.get(
      `${API_URL}/student-feedback/check/${payload.classid}/${payload.teacher_id}/${payload.student_id}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (checkRes.data.exists) {
      await axios.put(`${API_URL}/student-feedback/${checkRes.data.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Feedback updated successfully');
    } else {
      await axios.post(`${API_URL}/student-feedback`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success('Feedback submitted successfully');
    }

    handleCloseFeedback();
  } catch (error) {
    console.error('Error submitting feedback:', error);
    message.error('Failed to submit feedback');
  }
};

    const getRegisteredStudents = (groupId) => {
    const group = registrationsCount.find(rc => rc.groupid === groupId);
    return group ? group.registered_students : 0;
  };
  
  const getPlatformDisplay = (platform, t) => {
  switch (platform) {
    case 'onsite':
      return (
        <Tag color="blue" icon={<EnvironmentOutlined />}>
          {t('onsite')}
        </Tag>
      );
    case 'online':
      return (
        <Tag color="green" icon={<VideoCameraOutlined />}>
          {t('online')}
        </Tag>
      );
    case 'athome':
      return (
        <Tag color="orange" icon={<HomeOutlined />}>
          {t('athome')}
        </Tag>
      );
    default:
      return (
        <Tag color="default" icon={<QuestionCircleOutlined />}>
          -
        </Tag>
      );
  }
};

  const today = new Date().setHours(0, 0, 0, 0);

  // Separate class groups into future and past
  const futureClassGroups = classGroups.filter(
    classGroup => new Date(classGroup.start_date).getTime() >= today
  );
  const pastClassGroups = classGroups.filter(
    classGroup => new Date(classGroup.start_date).getTime() < today
  );

  // Sort the class groups
  const sortedFutureClassGroups = [...futureClassGroups].sort(
    (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
  );
  const sortedPastClassGroups = [...pastClassGroups].sort(
    (a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
  );

    const filteredFutureClassGroups = sortedFutureClassGroups.filter(group =>
  group.class_code.toLowerCase().includes(futureSearchText.toLowerCase())
);

const filteredPastClassGroups = sortedPastClassGroups.filter(group =>
  group.class_code.toLowerCase().includes(pastSearchText.toLowerCase())
);

  if (error) return <p>{error}</p>;
  if (!teacher) return <p>{t('loading')}</p>;

  const columns: any[] = [
    {
      title: t('classCode'),
      dataIndex: 'class_code',
      key: 'class_code',
      fixed: 'left',
      render: (class_code, record) => (
        <Link
          to={`/classdetails/${record.groupid}`}
          style={{ textDecoration: 'none', fontWeight: 'bold' }}
        >
          {class_code}
        </Link> 
      ),
    },
    {
      title: t('subjects'),
      dataIndex: 'subjects',
      key: 'subjects',
      render: subjects => subjects.map(subject => subject.title).join(', '),
    },
      {
      title: t('registeredStudents'),
      dataIndex: 'groupid',
      key: 'groupid',
      render: getRegisteredStudents,
    },
    {
      title: t('startDate'),
      dataIndex: 'start_date',
      key: 'start_date',
      render: formatDate,
    },
    {
      title: t('endDate'),
      dataIndex: 'end_date',
      key: 'end_date',
      render: formatDate,
    },
    {
      title: t('totalHours'),
      dataIndex: 'total_hours',
      key: 'total_hours',
      responsive: ['md'],
      render: formatScheduleHour,
    },
    {
      title: t('My Hours'),
      dataIndex: 'teacher_hours',
      key: 'teacher_hours',
      responsive: ['lg'],
      render: formatScheduleHour,
    },
    {
      title: t('type'),
      dataIndex: 'isprivate',
      key: 'isprivate',
      render: isprivate => (
        <Tag color={isprivate ? 'blue' : 'green'}>
          {isprivate ? t('private') : t('group')}
        </Tag>
      ),
    },
    {
      title: t('platform'),
      key: 'platform',
      dataIndex: 'platform',
      width: 120,
      render: (platform) => getPlatformDisplay(platform, t),
    },
    
  ];

  return (
    <div>
      <Sidebar />
      <Header />
      <div style={{ background: '#f0f2f5', minHeight: '100vh', width: '98%' }}>
        {/* Teacher Header */}
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Card>
              <Row justify="space-between" align="middle">
                <Col xs={24} sm={16}>
                  <Space align="start" size={16}>
                    <Avatar size={64} src={teacher.photourl} icon={<UserOutlined />} />
                    <Space direction="vertical" size={0}>
                      <Title level={4} style={{ margin: 0 }}>
                        {teacher.firstname} {teacher.lastname} ({teacher.nickname})
                      </Title>
                      <Text type="secondary">
                        <CalendarOutlined /> {t('dateJoined')}: {formatDate(teacher.createdat)}
                      </Text>
                      <Tag color={teacher.online ? 'success' : 'default'}>
                        {teacher.online ? t('online') : t('offline')}
                      </Tag>
                    </Space>
                  </Space>
                </Col>
                <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                  <Text type="secondary">
                    {t('lastSignedIn')}: {teacher.last_signed_in ? formatDateTime(teacher.last_signed_in) : 'N/A'}
                  </Text>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
        <Card 
              title={
                <Space>
                  <CalendarOutlined />
                  <Text strong>{t("Today's Classes")}</Text>
                  <Badge count={todaysClasses.length} />
                </Space>
              }
              bordered={false}
              style={{ height: '700px' }}
              bodyStyle={{ padding: '12px', height: 'calc(100% - 57px)', overflowY: 'auto' }}
            >
              {todaysClasses.length === 0 ? (
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%',
                  color: '#999'
                }}>
                  <CalendarOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Text type="secondary">{t('No classes scheduled for today')}</Text>
                </div>
              ) : (
                <List
                  dataSource={todaysClasses}
                  renderItem={(classItem, index) => (
                    <List.Item key={classItem.classid} style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                      <Card 
                        size="small" 
                        style={{ width: '100%', background: '#fafafa' }}
                        bodyStyle={{ padding: '12px' }}
                      >
                        <Row justify="space-between" align="top">
                          <Col span={24}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              {/* Class Info Row */}
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Link
                                    to={`/classdetails/${classItem.groupid}`}
                                    style={{ fontWeight: 'bold', fontSize: '14px' }}
                                  >
                                    {classItem.class_code}
                                  </Link>
                                </Col>
                                <Col>
                                  <Tag color="blue" style={{ margin: 0, fontSize: '11px' }}>
                                    {classItem.schedule_time && classItem.end_time ? 
                                      `${formatTimeHHMM(classItem.schedule_time)} - ${formatTimeHHMM(classItem.end_time)}` : 
                                      'Time TBD'
                                    }
                                  </Tag>
                                </Col>
                              </Row>

                              {/* Details Row */}
                              <Row justify="space-between" align="middle">
                                <Col>
                                  <Space size={16}>
                                    <Space size={4}>
                                      <BookOutlined style={{ fontSize: '12px', color: '#666' }} />
                                      <Text style={{ fontSize: '12px', color: '#666' }}>
                                        {classItem.subject_name || 'N/A'}
                                      </Text>
                                    </Space>
                                    <Space size={4}>
                                      <TeamOutlined style={{ fontSize: '12px', color: '#666' }} />
                                      <Text style={{ fontSize: '12px', color: '#666' }}>
                                        Room: {classItem.classroom_number || 'N/A'}
                                      </Text>
                                    </Space>
                                  </Space>
                                </Col>
                                <Col>
                                  <Space size={4}>
                                    {getPlatformDisplay(classItem.platform, t)}
                                    <Tag color={classItem.isprivate ? 'blue' : 'green'} style={{ margin: 0, fontSize: '10px' }}>
                                      {classItem.isprivate ? t('private') : t('group')}
                                    </Tag>
                                  </Space>
                                </Col>
                              </Row>

                              {/* Action Buttons Row */}
                              <Row style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e8e8e8' }}>
                                <Col span={24}>
                                  <Space size={8} style={{ width: '100%', justifyContent: 'flex-end' }}>
                                    <Button
                                      size="small"
                                      type="primary"
                                      ghost
                                      icon={<LoginOutlined />}
                                      onClick={() => handleShowModal(classItem)}
                                    >
                                      {t('Attendance')}
                                    </Button>
                                    <Button
                                      size="small"
                                      type="default"
                                      icon={<BookOutlined />}
                                      onClick={() => handleOpenFeedback(classItem)}
                                    >
                                      {t('Feedback')}
                                    </Button>
                                  </Space>
                                </Col>
                              </Row>
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </List.Item>
                  )}
                />
              )}
            </Card>
            
          </Col>
          
          <Col xs={24} lg={16}>
          <Card bordered={false}>
          <Row gutter={[24, 24]}>
  {/* Statistics Row */}
  <Col xs={24}>
    <Row gutter={[16, 16]}>
      {/* Main Hours Card */}
      <Col xs={24} md={8}>
        <Card
          style={{
            background: 'linear-gradient(135deg, #1890ff, #096dd9)',
            color: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '16px' }}>
                  {t('Current Month Hours')}
                </Text>
              </Col>
              <Col>
                <ClockCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
              </Col>
            </Row>
            
            <Statistic
              value={formatMinutesToHours(totalMonthlyHours)}
              valueStyle={{ 
                color: 'white', 
                fontSize: '36px',
                fontWeight: 'bold'
              }}
              prefix={<HourglassOutlined />}
            />



          </Space>
        </Card>
      </Col>

      {/* Average Hours/Week */}
      <Col xs={24} md={8}>
        <Card 
          bordered={false} 
          style={{ 
            background: 'linear-gradient(135deg, #fce3b4, #f9d69e)',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Statistic
            title={t('Average Hours/Week')}
            value={(totalMonthlyHours / (60 * 4)).toFixed(1)}
            suffix={t('hr')}
            prefix={<FieldTimeOutlined style={{ color: '#a17328 ' }} />}
            valueStyle={{ color: '#a17328 ', fontSize: '36px' }}
          />
        </Card>
      </Col>

       {/* Average Hours/Day */}
       <Col xs={24} md={8}>
  <Card 
    bordered={false} 
    style={{ 
      background: 'linear-gradient(135deg, #E8D3EF, #D7BDE2)',// Soft Lavender
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <Statistic
      title={t('Average Hours/Day')} // Updated title
      value={(totalMonthlyHours / (60 * moment().daysInMonth())).toFixed(2)} // Format to two decimal places
      suffix={t('hr')}
      prefix={<FieldTimeOutlined style={{ color: '#6F42C1' }} />} // Soft Violet Icon
      valueStyle={{ color: '#6F42C1', fontSize: '36px' }} // Matching Violet Text
    />
  </Card>
</Col>
    </Row>
  </Col>

  {/* Chart Row - Full Width */}
  <Col xs={24}>
    <Card
      title={
        <Space>
          <BarChartOutlined style={{ fontSize: '20px' }} />
          <span>{t('Monthly Hours Trend')}</span>
        </Space>
      }
      bordered={false}
    >
      <MonthlyHoursTrends teacherId={userId} />
    </Card>
  </Col>
</Row>
</Card>
          </Col>
        </Row>

        <div>
      {/* Future Classes Table */}
      <Card style={{ marginTop: 16 }}>
        <h3>{t('upcomingClasses')}</h3>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Input.Search
            placeholder="Search by student name (course)"
            value={futureSearchText}
            onChange={e => setFutureSearchText(e.target.value)}
            style={{ width: 350, marginBottom: 16 }}
            allowClear
          />
        <Table
          columns={columns}
          dataSource={filteredFutureClassGroups}
          rowKey="groupid"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
        </div>
      </Card>

      {/* Past Classes Table */}
      <Card style={{ marginTop: 16 }}>
        <h3>{t('All Classes')}</h3>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <Input.Search
            placeholder="Search by student name (course)"
            value={pastSearchText}
            onChange={e => setPastSearchText(e.target.value)}
            style={{ width: 350, marginBottom: 16 }}
            allowClear
          />
        <Table
          columns={columns}
          dataSource={filteredPastClassGroups}
          rowKey="groupid"
          bordered
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
        </div>
      </Card>
    </div>
      </div>
      {/* Attendance Drawer */}
<Drawer
  title={t("Attendance")}
  open={selectedClass !== null}
  onClose={() => setSelectedClass(null)}
  width={window.innerWidth < 768 ? '100%' : 600}
  placement="right"
>
  {selectedClass && (
    <>
      {/* Class Info Header */}
      <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
        <Text strong style={{ color: '#1890ff' }}>{selectedClass.subject_name}</Text>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          {moment(selectedClass.date).format('DD-MMM-YYYY')} • {formatTimeHHMM(selectedClass.schedule_time)} - {formatTimeHHMM(selectedClass.end_time)}
        </div>
      </div>

      {/* Students List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {registeredStudents.map((student) => {
          const savedRecord = savedAttendances[student.student_id];
          const currentAttendance = attendance[student.student_id];
          
          return (
            <div 
              key={student.student_id} 
              style={{ 
                border: '1px solid #e8e8e8', 
                borderRadius: 8, 
                padding: 12,
                background: 'white'
              }}
            >
              {savedRecord && !editMode[student.student_id] ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    <div>
                      <Text strong style={{ color: '#1890ff' }}>{student.student_name} ({student.nickname})</Text>
                      <div style={{ fontSize: 11, color: '#999' }}>ID: {student.student_id}</div>
                    </div>
                    <Space size={4}>
                      <Tag color={savedRecord.status === 'Present' ? 'green' : savedRecord.status === 'Late_15' ? 'orange' : savedRecord.status === 'Late_Over15' ? 'volcano' : 'red'}>
                        {savedRecord.status === 'Late_15' ? 'Late ≤ 15 M' : savedRecord.status === 'Late_Over15' ? 'Late > 15 M' : savedRecord.status}
                      </Tag>
                      <Button size="small" onClick={() => handleEdit(student.student_id)}>Edit</Button>
                    </Space>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#666', paddingTop: 8, borderTop: '1px solid #f0f0f0', flexWrap: 'wrap' }}>
                    <span>Homework: <Tag color={savedRecord.homework === 'YES' ? 'green' : savedRecord.homework === 'NO' ? 'red' : 'default'}>{savedRecord.homework}</Tag></span>
                    <span>{moment(savedRecord.timestamp).format('DD-MMM-YYYY HH:mm')}</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Text strong style={{ color: '#1890ff', display: 'block', marginBottom: 8 }}>{student.student_name} ({student.nickname})</Text>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#666' }}>Status</label>
                      <Select
                        value={currentAttendance?.status || ''}
                        onChange={(value) => handleAttendanceChange(student.student_id, 'status', value)}
                        placeholder="Select"
                        style={{ width: '100%', marginTop: 2 }}
                        size="small"
                      >
                        <Select.Option value="Present">{t('present')}</Select.Option>
                        <Select.Option value="Absent">{t('absent')}</Select.Option>
                        <Select.Option value="Late_15">Late ≤ 15 M</Select.Option>
                        <Select.Option value="Late_Over15">Late {'>'} 15 M</Select.Option>
                      </Select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#666' }}>Homework</label>
                      <Select
                        value={homework[student.student_id] || ''}
                        onChange={(value) => handleHomeworkChange(student.student_id, value)}
                        placeholder="Select"
                        style={{ width: '100%', marginTop: 2 }}
                        size="small"
                      >
                        <Select.Option value="YES">YES</Select.Option>
                        <Select.Option value="NO">NO</Select.Option>
                        <Select.Option value="N/A">N/A</Select.Option>
                      </Select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => editMode[student.student_id] ? handleUpdate(student.student_id) : handleSaveAttendance(student.student_id)}
                        disabled={savingInProgress || !currentAttendance?.status}
                        block
                      >
                        {editMode[student.student_id] ? 'Update' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  )}
</Drawer>

{/* Feedback Drawer */}
<Drawer
  title={t("Submit Feedback")}
  open={feedbackVisible}
  onClose={handleCloseFeedback}
  width={window.innerWidth < 768 ? '100%' : 600}
  footer={
    <div style={{ textAlign: 'right' }}>
      <Button onClick={handleCloseFeedback} style={{ marginRight: 8 }}>Cancel</Button>
      <Button type="primary" onClick={handleSubmitFeedback} disabled={isFeedbackApproved}>Submit</Button>
    </div>
  }
>
  {selectedClassFeedback && (
    <>
      <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
        <Text strong style={{ color: '#1890ff' }}>{selectedClassFeedback.subject_name}</Text>
        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
          {moment(selectedClassFeedback.date).format('DD-MMM-YYYY')} • {selectedClassFeedback.class_code}
        </div>
      </div>

      {studentId && (
        isFeedbackApproved ? 
          <Tag color="green" style={{ marginBottom: 16 }}>Approved</Tag> : 
          <Tag color="orange" style={{ marginBottom: 16 }}>Pending Approval</Tag>
      )}

      <Form layout="vertical" disabled={isFeedbackApproved}>
        <Form.Item label="Student" required>
          <Select
            value={studentId}
            onChange={async (value) => {
              setStudentId(value);
              try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                  `${API_URL}/student-feedback/by-classid-student/${selectedClassFeedback.classid}/${value}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.data) {
                  setFeedbackData({
                    warmup: res.data.warmup || '',
                    task_1: res.data.task_1 || '',
                    task_2: res.data.task_2 || '',
                    task_3: res.data.task_3 || '',
                    homework: res.data.homework || '',
                    reflection: res.data.reflection || '',
                    comment: res.data.comment || ''
                  });
                  setIsFeedbackApproved(res.data.approve !== 'pending');
                } else {
                  setFeedbackData({ warmup: '', task_1: '', task_2: '', task_3: '', homework: '', reflection: '', comment: '' });
                  setIsFeedbackApproved(false);
                }
              } catch (err) {
                console.error('Failed to fetch feedback:', err);
              }
            }}
            placeholder="Select a student"
          >
            {studentsInClass.map((student) => (
              <Select.Option key={student.student_id} value={student.student_id}>
                ({student.nickname}) {student.student_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {['warmup', 'task_1', 'task_2', 'task_3', 'homework', 'reflection', 'comment'].map((field) => (
          <Form.Item key={field} label={field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}>
            <Input.TextArea
              value={feedbackData[field]}
              onChange={(e) => setFeedbackData({ ...feedbackData, [field]: e.target.value })}
              rows={2}
            />
          </Form.Item>
        ))}
      </Form>
    </>
  )}
</Drawer>
    </div>
  );
}

export default withRoleAccess(TeacherDashboard, ['superadmin', 'admin', 'teacher']);
